"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseProject = parseProject;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const allegroValidation_1 = require("./allegroValidation");
const AllegroOrchestratedTransformer_1 = require("./transformers/allegroOrchestratedTransformer/AllegroOrchestratedTransformer");
const AllegroResolveDesign_1 = require("./transformers/allegroOrchestratedTransformer/AllegroResolveDesign");
const sdaxArchive_1 = require("./parsers/sdax/sdaxArchive");
const cdszArchive_1 = require("./parsers/cdsz/cdszArchive");
const AllegroNetlistParser_1 = require("./parsers/allegro-netlist/AllegroNetlistParser");
const AllegroPartListParser_1 = require("./parsers/allegro-netlist/AllegroPartListParser");
const xconParser_1 = require("./parsers/xcon/xconParser");
const dxJsonParser_1 = require("./parsers/dx/dxJsonParser");
const parser_1 = require("./parsers/ascii/bracket/parser");
const decode_document_1 = require("./parsers/ascii/decode/decoders/decode-document");
const decode_master_document_1 = require("./parsers/ascii/decode/decoders/decode-master-document");
const AllegroRecordHelpers_1 = require("./transformers/allegroOrchestratedTransformer/AllegroRecordHelpers");
const helpers_1 = require("./parsers/ascii/decode/helpers");
const cssParser_1 = require("./parsers/css/cssParser");
const chipsPrtParser_1 = require("./parsers/chipsPrt/chipsPrtParser");
const styleParser_1 = require("./parsers/style/styleParser");
const dcfParser_1 = require("./parsers/dcf/dcfParser");
function getDefaultSettings() {
    return {
        transformOpts: {},
        allowPartialUpload: false,
    };
}
async function parseProject(def, initialSettings = {}) {
    const settings = { ...getDefaultSettings(), ...initialSettings };
    if (def.type !== 'allegro') {
        throw new Error(`Attempted to parse non-Allegro project def: ${def.type}`);
    }
    const parsedCpm = pullParsedCpm(def);
    const parsedCdsLib = pullParsedCdsLib(def);
    const sdaxBlob = await pullSdaxBytes(def);
    const cdszBlob = await pullCdszBytes(def);
    let packagerNetlist;
    let packagerPartList;
    if (cdszBlob !== null) {
        const extractedCdsz = (0, cdszArchive_1.extractCdsz)(cdszBlob);
        const netlistEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extractedCdsz, 'pstxnet.dat');
        if (netlistEntry) {
            packagerNetlist = (0, AllegroNetlistParser_1.parseAllegroNetlist)((0, cdszArchive_1.readCdszEntryAsText)(netlistEntry));
        }
        const partListEntry = (0, cdszArchive_1.findCdszEntryByBasename)(extractedCdsz, 'pstxprt.dat');
        if (partListEntry) {
            packagerPartList = (0, AllegroPartListParser_1.parseAllegroPartList)((0, cdszArchive_1.readCdszEntryAsText)(partListEntry));
        }
    }
    const symbolCssByCell = await pullSymbolCssMap(def, parsedCdsLib);
    const chipsPrtByCellName = await pullChipsPrtMap(def);
    let extractedSdax = null;
    let parsedXcon = null;
    let parsedDx = null;
    let masterBundle = null;
    const sheetPages = [];
    if (sdaxBlob !== null) {
        extractedSdax = (0, sdaxArchive_1.extractSdax)(sdaxBlob);
        parsedXcon = parseXconFromSdax(extractedSdax, parsedCpm.designName);
        parsedDx = parseDxFromSdax(extractedSdax, parsedCpm.designName);
        masterBundle = decodeMasterFromSdax(extractedSdax, parsedCpm.designName);
        sheetPages.push(...decodeSheetPagesFromSdax(extractedSdax, masterBundle, settings.allowPartialUpload));
    }
    const { styleMap: styles, styleWarnings } = await pullStyleMap(def, extractedSdax);
    if (!settings.allowPartialUpload) {
        assertCompleteUpload({ parsedCpm, parsedCdsLib, sdaxBlob, extractedSdax, masterBundle, sheetPages });
    }
    const instanceHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildInstanceHashMap)(masterBundle.body);
    const netHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildNetHashMap)(masterBundle.body);
    const styleAssignmentByKey = buildStyleAssignmentMap(masterBundle?.body ?? null);
    const parsedDcf = extractedSdax !== null ? parseDcfFromSdax(extractedSdax, parsedCpm.designName) : null;
    const instanceSectionMap = parsedDcf?.sectionMap ?? new Map();
    const instancePinMap = parsedDcf?.instancePinMap ?? new Map();
    const instanceAttributes = parsedDcf?.instanceAttributes ?? new Map();
    const netAttributesByName = parsedDcf?.netAttributesByPhysName ?? new Map();
    const physNetNameByXconId = parsedDcf?.physNetNameByXconId ?? new Map();
    const xconPinNetByInstance = buildXconPinNetByInstance(parsedXcon, physNetNameByXconId);
    const cellInstanceIds = buildCellInstanceIds(sheetPages, instanceHashMap);
    const resolved = (0, AllegroResolveDesign_1.resolveAllegroProject)({
        projectName: parsedCpm.designName,
        parsedXcon,
        parsedDx,
        instanceHashMap,
        netHashMap,
        instanceSectionMap,
        instancePinMap,
        instanceAttributes,
        netAttributesByName,
        physNetNameByXconId,
        xconPinNetByInstance,
        cellInstanceIds,
        symbolCssByCell,
        chipsPrtByCellName,
        styles,
        styleAssignmentByKey,
        sheetPages,
    });
    const rootContext = {
        parsedDef: def,
        options: settings.transformOpts,
        parsedCpm,
        parsedCdsLib,
        parsedXcon,
        parsedDx,
        decodedMaster: masterBundle?.body ?? null,
        instanceHashMap,
        netHashMap,
        instanceSectionMap,
        instancePinMap,
        instanceAttributes,
        netAttributesByName,
        physNetNameByXconId,
        xconPinNetByInstance,
        cellInstanceIds,
        sheetPages,
        symbolCssByCell,
        chipsPrtByCellName,
        styles,
        styleAssignmentByKey,
        componentsByCellName: new Map(),
        resolved,
        resolveImageBytes: buildImageResolver(extractedSdax),
        packagerNetlist,
        packagerPartList,
    };
    const transformResult = (0, AllegroOrchestratedTransformer_1.transformAllegroProject)(rootContext);
    const projectModel = transformResult.projectModels[0] ?? null;
    if (projectModel !== null) {
        attachParseResults(projectModel, masterBundle, sheetPages, packagerNetlist, packagerPartList, styleWarnings);
    }
    return {
        projectModel,
        extractedImages: transformResult.extractedImages,
    };
}
function buildXconPinNetByInstance(parsedXcon, physNetNameByXconId) {
    const out = new Map();
    const design = parsedXcon?.designs[0];
    if (design === undefined) {
        return out;
    }
    for (const instance of design.instances.values()) {
        const cell = design.cells.get(instance.cellId);
        if (cell === undefined) {
            continue;
        }
        const termIdToName = new Map();
        for (const term of cell.terms) {
            if (term.id.length > 0 && term.name.length > 0) {
                termIdToName.set(term.id, term.name.toLowerCase());
            }
        }
        const pinNetMap = new Map();
        for (const pin of instance.pins) {
            const pinName = termIdToName.get(pin.termId);
            if (pinName === undefined) {
                continue;
            }
            const netId = pin.netIds[0];
            if (netId === undefined || netId.length === 0) {
                continue;
            }
            const netName = physNetNameByXconId.get(netId) ?? design.nets.get(netId)?.name;
            if (netName === undefined || netName.length === 0) {
                continue;
            }
            pinNetMap.set(pinName, netName);
        }
        if (pinNetMap.size > 0) {
            out.set(instance.id, pinNetMap);
        }
    }
    return out;
}
function buildCellInstanceIds(sheetPages, instanceHashMap) {
    const result = new Map();
    for (const page of sheetPages) {
        for (const instance of (0, AllegroRecordHelpers_1.iterateComponentInstances)(page.body)) {
            const key = `${page.pageNum}:${instance.placementHash}`;
            const xconId = instanceHashMap.get(key);
            if (xconId === undefined) {
                continue;
            }
            const cellName = (0, AllegroRecordHelpers_1.modelNameOf)(instance.modelRef);
            let ids = result.get(cellName);
            if (ids === undefined) {
                ids = [];
                result.set(cellName, ids);
            }
            if (!ids.includes(xconId)) {
                ids.push(xconId);
            }
        }
    }
    return result;
}
function assertCompleteUpload(state) {
    const errors = [];
    if (state.parsedCpm.designName === '' || state.parsedCpm.designDb === '') {
        errors.push('missing or invalid `.cpm` (no design_name / design_db)');
    }
    if (state.parsedCdsLib.defines.size === 0 && state.parsedCdsLib.includes.length === 0) {
        errors.push('missing or empty `cds.lib`');
    }
    if (state.sdaxBlob === null || state.extractedSdax === null) {
        errors.push('missing `.sdax` schematic database');
    }
    if (state.extractedSdax !== null) {
        if (state.masterBundle === null) {
            errors.push('`.sdax` is missing the master `<design>.ascii` file');
        }
        else if (state.masterBundle.body.pageRefs.length === 0) {
            errors.push('master `<design>.ascii` page table is empty');
        }
        else {
            const presentPageBasenames = new Set();
            for (const pageEntry of state.sheetPages) {
                presentPageBasenames.add(pageEntry.pageFileName);
            }
            const missingPages = [];
            for (const ref of state.masterBundle.body.pageRefs) {
                if (!presentPageBasenames.has(ref.pageFile)) {
                    missingPages.push(`${ref.pageName}(#${ref.pageNum}) → ${ref.pageFile}`);
                }
            }
            if (missingPages.length > 0) {
                errors.push(`master references ${missingPages.length} missing sheet(s): ${missingPages.join(', ')}`);
            }
            const referencedPageBasenames = new Set(state.masterBundle.body.pageRefs.map((r) => r.pageFile));
            const orphans = Array.from(presentPageBasenames).filter((p) => !referencedPageBasenames.has(p));
            if (orphans.length > 0) {
                errors.push(`${orphans.length} sheet(s) present but not referenced by master: ${orphans.join(', ')}`);
            }
        }
    }
    if (errors.length > 0) {
        throw new Error(`Allegro X System Capture upload is incomplete:\n  - ${errors.join('\n  - ')}\n` +
            'Pass { allowPartialUpload: true } if intentionally parsing a partial archive.');
    }
}
async function pullSymbolCssMap(def, parsedCdsLib) {
    const out = new Map();
    if (def.other === undefined) {
        return out;
    }
    const libraryByPathPrefix = new Map();
    for (const [libName, libPath] of parsedCdsLib.defines.entries()) {
        const normalized = libPath.replace(/\/+$/, '');
        libraryByPathPrefix.set(normalized, libName);
    }
    for (const descriptor of Object.values(def.other)) {
        if (descriptor.validationResult?.data?.fileFormat.type !== allegroValidation_1.AllegroFileType.Css) {
            continue;
        }
        const relPath = descriptor.originalFileName;
        if (!relPath.toLowerCase().endsWith('/symbol.css')) {
            continue;
        }
        const segments = relPath.split('/');
        if (segments.length < 4) {
            continue;
        }
        const view = (0, helpers_1.decodeCadenceName)(segments[segments.length - 2]);
        const name = (0, helpers_1.decodeCadenceName)(segments[segments.length - 3]);
        const libraryDir = segments.slice(0, segments.length - 3).join('/');
        const libraryName = libraryByPathPrefix.get(libraryDir) ?? libraryByPathPrefix.get(libraryDir.replace(/^.*\//, ''));
        if (libraryName === undefined) {
            continue;
        }
        const text = await descriptor.contents.text();
        out.set(`${libraryName}__${name}__${view}`, (0, cssParser_1.parseSymbolCss)(text));
    }
    return out;
}
async function pullChipsPrtMap(def) {
    const out = new Map();
    if (def.other === undefined) {
        return out;
    }
    for (const descriptor of Object.values(def.other)) {
        if (descriptor.validationResult?.data?.fileFormat.type !== allegroValidation_1.AllegroFileType.ChipsPrt) {
            continue;
        }
        const text = await descriptor.contents.text();
        const parsed = (0, chipsPrtParser_1.parseChipsPrt)(text);
        for (const [name, part] of parsed.parts) {
            out.set(name, part);
        }
    }
    return out;
}
async function pullStyleMap(def, extractedSdax) {
    const design = new Map();
    const cells = new Map();
    const styleWarnings = [];
    const ingest = (path, text) => {
        const parsed = (0, styleParser_1.parseStyleFile)(text);
        styleWarnings.push(...parsed.warnings);
        const scopeKey = scopeForStylePath(path);
        if (scopeKey === 'design') {
            for (const [name, sd] of parsed.styles) {
                design.set(name, sd);
            }
        }
        else {
            let cellMap = cells.get(scopeKey);
            if (cellMap === undefined) {
                cellMap = new Map();
                cells.set(scopeKey, cellMap);
            }
            for (const [name, sd] of parsed.styles) {
                cellMap.set(name, sd);
            }
        }
    };
    for (const descriptor of Object.values(def.other ?? {})) {
        if (descriptor.validationResult?.data?.fileFormat.type !== allegroValidation_1.AllegroFileType.Style) {
            continue;
        }
        const text = await descriptor.contents.text();
        ingest(descriptor.originalFileName, text);
    }
    if (extractedSdax !== null) {
        for (const innerPath of (0, sdaxArchive_1.findEntriesByExtension)(extractedSdax, '.style')) {
            const entry = extractedSdax.entries.get(innerPath);
            if (entry === undefined) {
                continue;
            }
            ingest(innerPath, (0, sdaxArchive_1.readEntryAsText)(entry));
        }
    }
    return { styleMap: { design, cells }, styleWarnings };
}
function scopeForStylePath(relPath) {
    const lower = relPath.toLowerCase().replace(/\\/g, '/');
    if (/(^|\/)worklib\//.test(lower)) {
        return 'design';
    }
    const base = relPath.split('/').pop() ?? relPath;
    return `cell:${base.replace(/\.style$/i, '').toLowerCase()}`;
}
function buildStyleAssignmentMap(master) {
    const out = new Map();
    if (master === null) {
        return out;
    }
    for (const row of master.styleAssignments) {
        out.set(`${row.pageNum}:${row.rowIndex}`, row.styleName);
    }
    return out;
}
function pullParsedCpm(def) {
    for (const descriptor of Object.values(def.other ?? {})) {
        const data = descriptor.validationResult?.data;
        if (data?.fileFormat.type === allegroValidation_1.AllegroFileType.Cpm) {
            const parsed = data.metadata[allegroValidation_1.AllegroProjectMetadataKey.ParsedCpm];
            if (parsed !== undefined) {
                return parsed;
            }
        }
    }
    return {
        sections: new Map(),
        designName: '',
        designLibrary: '',
        libraries: [],
        cpmVersion: '',
        designDb: '',
    };
}
function pullParsedCdsLib(def) {
    for (const descriptor of Object.values(def.other ?? {})) {
        const data = descriptor.validationResult?.data;
        if (data?.fileFormat.type === allegroValidation_1.AllegroFileType.CdsLib) {
            const parsed = data.metadata[allegroValidation_1.AllegroProjectMetadataKey.ParsedCdsLib];
            if (parsed !== undefined) {
                return parsed;
            }
        }
    }
    return { defines: new Map(), includes: [] };
}
async function pullSdaxBytes(def) {
    for (const descriptor of Object.values(def.schematics)) {
        if (isSdaxDescriptor(descriptor)) {
            const buffer = await descriptor.contents.arrayBuffer();
            return new Uint8Array(buffer);
        }
    }
    for (const descriptor of Object.values(def.other ?? {})) {
        if (isSdaxDescriptor(descriptor)) {
            const buffer = await descriptor.contents.arrayBuffer();
            return new Uint8Array(buffer);
        }
    }
    return null;
}
function isSdaxDescriptor(descriptor) {
    return descriptor.validationResult?.data?.fileFormat.type === allegroValidation_1.AllegroFileType.Sdax;
}
async function pullCdszBytes(def) {
    for (const descriptor of Object.values(def.other ?? {})) {
        if (descriptor.validationResult?.data?.fileFormat.type === allegroValidation_1.AllegroFileType.Cdsz) {
            const buffer = await descriptor.contents.arrayBuffer();
            return new Uint8Array(buffer);
        }
    }
    return null;
}
function parseXconFromSdax(extracted, designName) {
    const candidates = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.xcon');
    if (candidates.length === 0) {
        return null;
    }
    let selectedPath = null;
    if (designName.length > 0) {
        const wantedSuffix = `${designName.toLowerCase()}.xcon`;
        for (const path of candidates) {
            if (path.toLowerCase().endsWith(wantedSuffix)) {
                selectedPath = path;
                break;
            }
        }
    }
    selectedPath ??= candidates[0];
    const entry = extracted.entries.get(selectedPath);
    if (entry === undefined) {
        return null;
    }
    return (0, xconParser_1.parseXcon)((0, sdaxArchive_1.readEntryAsText)(entry));
}
function parseDxFromSdax(extracted, designName) {
    const candidates = (0, sdaxArchive_1.findEntriesByExtension)(extracted, 'dx.json');
    if (candidates.length === 0) {
        return null;
    }
    let selectedPath = null;
    if (designName.length > 0) {
        const wantedSuffix = `${designName.toLowerCase()}dx.json`;
        for (const path of candidates) {
            if (path.toLowerCase().endsWith(wantedSuffix)) {
                selectedPath = path;
                break;
            }
        }
    }
    selectedPath ??= candidates[0];
    const entry = extracted.entries.get(selectedPath);
    if (entry === undefined) {
        return null;
    }
    return (0, dxJsonParser_1.parseDx)((0, sdaxArchive_1.readEntryAsText)(entry));
}
function parseDcfFromSdax(extracted, designName) {
    const candidates = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.dcf');
    if (candidates.length === 0) {
        return null;
    }
    let selectedPath = null;
    if (designName.length > 0) {
        const wantedSuffix = `${designName.toLowerCase()}.dcf`;
        for (const path of candidates) {
            if (path.toLowerCase().endsWith(wantedSuffix)) {
                selectedPath = path;
                break;
            }
        }
    }
    selectedPath ??= candidates[0];
    const entry = extracted.entries.get(selectedPath);
    if (entry === undefined) {
        return null;
    }
    return (0, dcfParser_1.parseDcf)(entry.contents);
}
function decodeMasterFromSdax(extracted, designName) {
    const allAscii = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.ascii');
    const pagePathPattern = /page_file_\d+\.ascii$/i;
    let masterPath = null;
    if (designName.length > 0) {
        const wantedSuffix = `/${designName.toLowerCase()}.ascii`;
        for (const path of allAscii) {
            if (path.toLowerCase().endsWith(wantedSuffix) && !pagePathPattern.test(path)) {
                masterPath = path;
                break;
            }
        }
    }
    if (masterPath === null) {
        const pageDirs = new Set();
        for (const path of allAscii) {
            if (pagePathPattern.test(path)) {
                const idx = path.lastIndexOf('/');
                pageDirs.add(idx >= 0 ? path.slice(0, idx) : '');
            }
        }
        for (const path of allAscii) {
            if (pagePathPattern.test(path) || path.toLowerCase().startsWith('cache/')) {
                continue;
            }
            const idx = path.lastIndexOf('/');
            const parent = idx >= 0 ? path.slice(0, idx) : '';
            if (pageDirs.has(parent)) {
                masterPath = path;
                break;
            }
        }
    }
    if (masterPath === null) {
        return null;
    }
    const entry = extracted.entries.get(masterPath);
    if (entry === undefined) {
        return null;
    }
    const text = (0, sdaxArchive_1.readEntryAsText)(entry);
    const doc = (0, decode_document_1.decodeDocument)(new parser_1.BracketParser(text).parse());
    if (doc.body.kind !== 'master') {
        return null;
    }
    return { doc, body: doc.body };
}
function decodeSheetPagesFromSdax(extracted, masterBundle, allowFilenameFallback) {
    const pagePathPattern = /page_file_(\d+)\.ascii$/i;
    const allAscii = (0, sdaxArchive_1.findEntriesByExtension)(extracted, '.ascii').filter((p) => pagePathPattern.test(p));
    const pageByBasename = new Map();
    for (const path of allAscii) {
        const basename = stripPageBasename(path);
        if (basename !== null) {
            pageByBasename.set(basename, path);
        }
    }
    const orderedRefs = [];
    if (masterBundle !== null && masterBundle.body.pageRefs.length > 0) {
        for (const ref of masterBundle.body.pageRefs) {
            const sourcePath = pageByBasename.get(ref.pageFile);
            if (sourcePath === undefined) {
                continue;
            }
            const fileNumMatch = /page_file_(\d+)/i.exec(ref.pageFile);
            const filePageNum = fileNumMatch === null ? '' : fileNumMatch[1];
            const sheetIdMatch = /\s*\((\d+)\)$/.exec(ref.pageName);
            const cleanedSheetName = sheetIdMatch === null ? ref.pageName : ref.pageName.slice(0, sheetIdMatch.index).trim();
            const sheetId = sheetIdMatch === null ? parseInt(ref.pageNum, 10) || 0 : parseInt(sheetIdMatch[1], 10);
            orderedRefs.push({
                sourcePath,
                pageFileName: ref.pageFile,
                pageNum: filePageNum,
                sheetName: cleanedSheetName,
                sheetId,
                pageType: 1,
            });
        }
    }
    else if (allowFilenameFallback) {
        const sorted = [...allAscii].sort((a, b) => sheetPageIndex(a, pagePathPattern) - sheetPageIndex(b, pagePathPattern));
        for (const sourcePath of sorted) {
            const basename = stripPageBasename(sourcePath) ?? '';
            const numMatch = pagePathPattern.exec(sourcePath);
            const pageNum = numMatch === null ? '' : numMatch[1];
            orderedRefs.push({
                sourcePath,
                pageFileName: basename,
                pageNum,
                sheetName: '',
                sheetId: 0,
                pageType: 0,
            });
        }
    }
    const instanceHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildInstanceHashMap)(masterBundle.body);
    const netHashMap = masterBundle === null ? new Map() : (0, decode_master_document_1.buildNetHashMap)(masterBundle.body);
    const pages = [];
    for (const ref of orderedRefs) {
        const entry = extracted.entries.get(ref.sourcePath);
        if (entry === undefined) {
            continue;
        }
        const text = (0, sdaxArchive_1.readEntryAsText)(entry);
        const doc = (0, decode_document_1.decodeDocument)(new parser_1.BracketParser(text).parse(), {
            instanceHashMap,
            netHashMap,
            pageNum: ref.pageNum,
        });
        if (doc.body.kind !== 'page') {
            continue;
        }
        pages.push({
            sourcePath: ref.sourcePath,
            pageFileName: ref.pageFileName,
            pageNum: ref.pageNum,
            sheetName: ref.sheetName,
            sheetId: ref.sheetId,
            pageType: ref.pageType,
            body: doc.body,
            decoded: doc,
        });
    }
    return pages;
}
function stripPageBasename(path) {
    const match = /(page_file_\d+)\.ascii$/i.exec(path);
    return match === null ? null : match[1];
}
function sheetPageIndex(path, pattern) {
    const match = pattern.exec(path);
    if (match === null) {
        return 0;
    }
    const n = parseInt(match[1], 10);
    return Number.isNaN(n) ? 0 : n;
}
function attachParseResults(projectModel, masterBundle, sheetPages, packagerNetlist, packagerPartList, styleWarnings) {
    const allWarnings = [];
    if (masterBundle !== null) {
        allWarnings.push(...masterBundle.doc.warnings);
    }
    for (const page of sheetPages) {
        allWarnings.push(...page.decoded.warnings);
    }
    if (allWarnings.length > 0) {
        const decodeResult = {
            type: 'allegro-decode',
            message: `Allegro ASCII decode completed with ${allWarnings.length} diagnostic(s)`,
            innerResults: allWarnings,
        };
        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(decodeResult);
        projectModel.processingResults.innerResults.push(decodeResult);
    }
    if (packagerNetlist !== undefined && packagerNetlist.processingResults.innerResults.length > 0) {
        projectModel.processingResults.innerResults.push(packagerNetlist.processingResults);
    }
    if (packagerPartList !== undefined && packagerPartList.processingResults.innerResults.length > 0) {
        projectModel.processingResults.innerResults.push(packagerPartList.processingResults);
    }
    if (styleWarnings.length > 0) {
        const styleResult = {
            type: 'allegro-style',
            message: `Allegro style parsing completed with ${styleWarnings.length} diagnostic(s)`,
            innerResults: styleWarnings,
        };
        (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(styleResult);
        projectModel.processingResults.innerResults.push(styleResult);
    }
    (0, sigma_engine_shared_1.computeAggragateMessageResultSeverity)(projectModel.processingResults);
}
function buildImageResolver(extracted) {
    if (extracted === null) {
        return () => null;
    }
    const byBasename = new Map();
    for (const [path, entry] of extracted.entries) {
        const slash = path.lastIndexOf('/');
        const base = slash >= 0 ? path.slice(slash + 1) : path;
        if (!byBasename.has(base)) {
            byBasename.set(base, entry.contents);
        }
    }
    return (filename) => byBasename.get(filename) ?? null;
}
//# sourceMappingURL=parseAllegroProject.js.map